import React, { useState, useEffect } from 'react';
import { Loader2, Users, UserCheck, History, Settings, TrendingUp, Clock, FileSpreadsheet, Shield, BadgeCheck } from 'lucide-react';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { useAuth } from './hooks/useAuth';
import { useFirestoreData } from './hooks/useFirestoreData';
import { collection, getDocs } from 'firebase/firestore';
import { db, APP_ID } from './config/firebase';
import {
  LoginScreen,
  Navbar,
  AdminSyncPanel,
  OwnerDashboard,
  TeamsView,
  PlayersView,
  PlayerActionModal,
  TeamModal,
  TransactionHistory,
  AuctionMode,
  OwnerAuctionView,
  SettingsPanel,
  TeamStats,
  UnsoldQueue,
  SoldPlayersList,
  ExportPanel,
  UserManagement,
  PublicDisplay
} from './components';
import { useAuction } from './hooks/useAuction';
import type { Player, Team, AppUser } from './types';

// --- Owner View with Auction Support ---
import type { AuctionSettings } from './types';

interface OwnerViewProps {
  currentUser: AppUser;
  teams: Team[];
  players: Player[];
  onLogout: () => void;
  shortlists: Record<string, string[]>;
  toggleShortlist: (teamId: string, playerId: string, user: AppUser) => Promise<void>;
  auctionSettings: AuctionSettings | null;
}

const OwnerView: React.FC<OwnerViewProps> = ({ currentUser, teams, players, shortlists, toggleShortlist, onLogout, auctionSettings }) => {
  const { auction } = useAuction({ players, teams, currentUser, auctionSettings });

  // Show auction view when auction is active
  if (auction.isActive) {
    return (
      <OwnerAuctionView
        players={players}
        teams={teams}
        currentUser={currentUser}
        shortlists={shortlists}
        onLogout={onLogout}
      />
    );
  }

  // Show normal owner dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar currentUser={currentUser} onLogout={onLogout} />
      <OwnerDashboard
        currentUser={currentUser}
        teams={teams}
        players={players}
        shortlists={shortlists}
        toggleShortlist={toggleShortlist}
      />
    </div>
  );
};

// --- Public Display Content ---
const PublicAppContent = () => {
   const { players, teams, fetchData, subscribeToRealtime, unsubscribeFromRealtime } = useFirestoreData();

   useEffect(() => {
     fetchData().then(() => subscribeToRealtime());
     return () => unsubscribeFromRealtime();
   }, [fetchData, subscribeToRealtime, unsubscribeFromRealtime]);

   return <PublicDisplay players={players} teams={teams} />;
};

// --- Main App Content ---
const AppContent = () => {
  const toast = useToast();
  const { currentUser, authLoading, handleLogout } = useAuth();
  const {
    players, teams, transactions, auctionSettings, loading, syncing, recalculatingBalances,
    shortlists,
    fetchData, syncFromWhatsApp, resetAndRefreshPlayers, createTeam, updateTeam, deleteTeam, recalculateTeamBalances, createPlayerUsers, bulkDeleteUsers, toggleShortlist,
    createPlayer, updatePlayer, deletePlayer,
    assignPlayerToTeam, removePlayerFromTeam, saveAuctionSettings,
    updateTransaction, deleteTransaction,
    subscribeToRealtime, unsubscribeFromRealtime
  } = useFirestoreData();

  // UI State
  const [activeTab, setActiveTab] = useState<'teams' | 'players' | 'sold' | 'stats' | 'unsold' | 'transactions' | 'export' | 'users' | 'settings'>('players');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalMode, setTeamModalMode] = useState<'create' | 'edit'>('create');
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [showAuctionMode, setShowAuctionMode] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [budget, setBudget] = useState(auctionSettings?.defaultTeamBudget || 10000);
  const [savingTeam, setSavingTeam] = useState(false);
  const [creatingPlayerUsers, setCreatingPlayerUsers] = useState(false);
  const [usersCount, setUsersCount] = useState<number | null>(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const baseBidPoints = auctionSettings?.minBidPoints ?? 100;
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [showTeamsBackButton, setShowTeamsBackButton] = useState(false);

  const handleCreatePlayerUsers = async () => {
    if (!currentUser) return;
    setCreatingPlayerUsers(true);

    const total = players.length;
    const progressToastId = toast.persist ? toast.persist(`Creating 0 of ${total} player logins...`, 'info') : undefined;

    const result = await createPlayerUsers(currentUser, (current, totalProgress) => {
      if (progressToastId && toast.update) {
        toast.update(progressToastId, `Creating ${current} of ${totalProgress} player logins...`, 'info');
      }
    });

    if (progressToastId && toast.update) {
      const summary = `Done. Created: ${result.created}, Existing: ${result.existing}, Invalid: ${result.invalid}, Failed: ${result.failed}`;
      toast.update(progressToastId, summary, result.failed ? 'warning' : 'success');
      // auto-dismiss after a short delay
      setTimeout(() => {
        toast.dismiss && toast.dismiss(progressToastId);
      }, 3000);
    }

    setCreatingPlayerUsers(false);
  };

  const handleRecalculateBalances = async () => {
    if (!currentUser) return;
    await recalculateTeamBalances(currentUser);
  };

  // Fetch users count for tab badge (lightweight)
  useEffect(() => {
    const fetchUsersCount = async () => {
      try {
        const snap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'users'));
        setUsersCount(snap.size);
      } catch (err) {
        console.error('Failed to fetch users count', err);
        setUsersCount(null);
      }
    };
    fetchUsersCount();
  }, [activeTab]);

  // Subscribe to real-time updates on auth
  useEffect(() => {
    if (currentUser) {
      // Initial fetch then subscribe to real-time
      fetchData().then(() => {
        subscribeToRealtime();
      });

      return () => {
        unsubscribeFromRealtime();
      };
    }
  }, [currentUser, fetchData, subscribeToRealtime, unsubscribeFromRealtime]);

  // Open modal for new team
  const handleOpenNewTeam = () => {
    setTeamModalMode('create');
    setEditingTeam(null);
    setTeamName('');
    setOwnerName('');
    setOwnerEmail('');
    setBudget(auctionSettings?.defaultTeamBudget || 10000);
    setShowTeamModal(true);
  };

  const handleViewTeamFromStats = (team: Team) => {
    setTeamSearchQuery(team.name);
    setShowTeamsBackButton(true);
    setActiveTab('teams');
  };

  const handleTeamsBackFromStats = () => {
    setActiveTab('stats');
    setTeamSearchQuery('');
    setShowTeamsBackButton(false);
  };

  // Open modal for editing team
  const handleEditTeam = (team: Team) => {
    setTeamModalMode('edit');
    setEditingTeam(team);
    setTeamName(team.name);
    setOwnerName(team.ownerName);
    setOwnerEmail(team.ownerEmail || '');
    setBudget(team.totalBudget);
    setShowTeamModal(true);
  };

  // Submit team (create or update)
  const handleSubmitTeam = async () => {
    if (!currentUser) return;
    setSavingTeam(true);

    let success = false;
    if (teamModalMode === 'create') {
      success = await createTeam(teamName, ownerName, ownerEmail, budget, currentUser);
    } else if (editingTeam) {
      success = await updateTeam(editingTeam.id, {
        name: teamName,
        ownerName: ownerName,
        ownerEmail: ownerEmail,
        totalBudget: budget
      }, currentUser);
    }

    setSavingTeam(false);
    if (success) {
      setShowTeamModal(false);
      setEditingTeam(null);
    }
  };

  // Delete team handler
  const handleDeleteTeam = async (teamId: string): Promise<boolean> => {
    if (!currentUser) return false;
    return await deleteTeam(teamId, currentUser);
  };

  // Wrapper functions to pass currentUser
  const handleAssignPlayer = async (player: Player, teamId: string, price: number) => {
    if (!currentUser) return;
    await assignPlayerToTeam(player, teamId, price, currentUser);
  };

  const handleRemovePlayer = async (player: Player) => {
    if (!currentUser) return;
    await removePlayerFromTeam(player, currentUser);
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!currentUser) {
    return <LoginScreen onError={(msg) => toast.error(msg)} />;
  }

  // Owner view - check for active auction first
  if (currentUser.role === 'owner') {
    return (
      <OwnerView
        currentUser={currentUser}
        teams={teams}
        players={players}
        shortlists={shortlists}
        toggleShortlist={toggleShortlist}
        onLogout={handleLogout}
        auctionSettings={auctionSettings}
      />
    );
  }

  // Auction mode handler
  const handleAuctionSell = async (player: Player, teamId: string, price: number) => {
    if (!currentUser) return;
    await assignPlayerToTeam(player, teamId, price, currentUser);
  };

  // Show Auction Mode if active
  if (showAuctionMode && currentUser) {
    return (
      <AuctionMode
        players={players}
        teams={teams}
        currentUser={currentUser}
        auctionSettings={auctionSettings}
        onSellPlayer={handleAuctionSell}
        onClose={() => setShowAuctionMode(false)}
      />
    );
  }

  // Admin view
  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar currentUser={currentUser} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Admin Controls */}
        <AdminSyncPanel
          syncing={syncing}
          onSync={activeTab === 'players' ? syncFromWhatsApp : undefined}
          onResetAndRefresh={activeTab === 'players' ? resetAndRefreshPlayers : undefined}
          onOpenNewTeam={handleOpenNewTeam}
          onCreatePlayerUsers={activeTab === 'players' ? handleCreatePlayerUsers : undefined}
          creatingPlayerUsers={creatingPlayerUsers}
          onRecalculateBalances={handleRecalculateBalances}
          recalculatingBalances={recalculatingBalances}
          onStartAuction={() => setShowAuctionMode(true)}
        />

        {/* Navigation */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm flex flex-wrap gap-2 relative">
          {[
            { key: 'players', label: 'Players', count: players.length, icon: <UserCheck className="w-4 h-4" /> },
            { key: 'teams', label: 'Teams', count: teams.length, icon: <Users className="w-4 h-4" /> },
            { key: 'export', label: 'Export', icon: <FileSpreadsheet className="w-4 h-4" /> },
            { key: 'stats', label: 'Stats', icon: <TrendingUp className="w-4 h-4" /> },
            { key: 'sold', label: 'Sold', count: players.filter(p => p.status === 'sold').length, icon: <BadgeCheck className="w-4 h-4" /> },
            { key: 'unsold', label: 'Unsold', count: players.filter(p => p.status === 'unsold').length, icon: <Clock className="w-4 h-4" /> },
          ].map(tab => {
            const isActive = activeTab === tab.key;
            const count = tab.count;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 shadow-[0_1px_6px_rgba(79,70,229,0.2)]'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-white text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                  {tab.icon}
                </div>
                <div className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  {typeof count === 'number' && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      isActive ? 'border-indigo-200 bg-white text-indigo-700' : 'border-slate-200 text-slate-600 bg-slate-50'
                    }`}>
                      {count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Settings dropdown */}
          <div className="relative">
            <button
              onClick={() => setSettingsMenuOpen(prev => !prev)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                ['transactions', 'users', 'settings'].includes(activeTab)
                  ? 'bg-indigo-50 text-indigo-700 shadow-[0_1px_6px_rgba(79,70,229,0.2)]'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                ['transactions', 'users', 'settings'].includes(activeTab) ? 'bg-white text-indigo-600' : 'bg-slate-100 text-slate-500'
              }`}>
                <Settings className="w-4 h-4" />
              </div>
              <span>Settings</span>
            </button>
            {settingsMenuOpen && (
              <div className="absolute mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-10">
                {[
                  { key: 'transactions', label: `Transactions (${transactions.length})`, icon: <History className="w-4 h-4" /> },
                  { key: 'users', label: `Users ${usersCount !== null ? `(${usersCount})` : ''}`, icon: <Shield className="w-4 h-4" /> },
                  { key: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
                ].map(item => {
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => { setActiveTab(item.key as typeof activeTab); setSettingsMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                        isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'teams' && (
          <TeamsView
            teams={teams}
            players={players}
            currentUser={currentUser}
            onManagePlayer={setSelectedPlayer}
            onEditTeam={handleEditTeam}
            onDeleteTeam={handleDeleteTeam}
            onCreateTeam={handleOpenNewTeam}
            shortlists={shortlists}
            initialSearchQuery={teamSearchQuery}
            showBackButton={showTeamsBackButton}
            onBack={handleTeamsBackFromStats}
          />
        )}
        {activeTab === 'players' && (
          <PlayersView
            players={players}
            teams={teams}
            currentUser={currentUser}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onManagePlayer={setSelectedPlayer}
            onRefresh={fetchData}
            loading={loading}
            onCreatePlayer={(data) => createPlayer(data, currentUser)}
            onUpdatePlayer={(id, updates) => updatePlayer(id, updates, currentUser)}
            onDeletePlayer={(id) => deletePlayer(id, currentUser)}
          />
        )}
        {activeTab === 'sold' && (
          <SoldPlayersList players={players} teams={teams} />
        )}
        {activeTab === 'stats' && (
          <TeamStats teams={teams} players={players} onViewTeam={handleViewTeamFromStats} />
        )}
        {activeTab === 'unsold' && (
          <UnsoldQueue
            players={players}
            teams={teams}
            currentUser={currentUser}
            onManagePlayer={setSelectedPlayer}
          />
        )}
        {activeTab === 'transactions' && (
          <TransactionHistory
            transactions={transactions}
            onUpdate={updateTransaction}
            onDelete={deleteTransaction}
            isAdmin={currentUser?.role === 'admin' || currentUser?.role === 'sub_admin'}
          />
        )}
        {activeTab === 'export' && (
          <ExportPanel players={players} teams={teams} transactions={transactions} />
        )}
        {activeTab === 'users' && (
          <UserManagement
            teams={teams}
            currentUser={currentUser}
            onBulkDeletePlayers={() => currentUser && bulkDeleteUsers(currentUser, 'player')}
            onBulkDeleteOwners={() => currentUser && bulkDeleteUsers(currentUser, 'owner')}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsPanel
            auctionSettings={auctionSettings}
            currentUser={currentUser}
            onSave={saveAuctionSettings}
          />
        )}
      </main>

      {/* Modals */}
      <PlayerActionModal
        player={selectedPlayer}
        teams={teams}
        isOpen={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        onAssign={handleAssignPlayer}
        onRemove={handleRemovePlayer}
        basePrice={baseBidPoints}
      />

      <TeamModal
        isOpen={showTeamModal}
        onClose={() => {
          setShowTeamModal(false);
          setEditingTeam(null);
        }}
        onSubmit={handleSubmitTeam}
        teamName={teamName}
        setTeamName={setTeamName}
    ownerName={ownerName}
    setOwnerName={setOwnerName}
    ownerEmail={ownerEmail}
    setOwnerEmail={setOwnerEmail}
    budget={budget}
    setBudget={setBudget}
    mode={teamModalMode}
    editingTeam={editingTeam}
    saving={savingTeam}
      />
    </div>
  );
};

// --- App Wrapper with Toast Provider ---
const App = () => {
  const isDisplayMode = new URLSearchParams(window.location.search).get('display') === 'true';

  return (
    <ToastProvider>
      {isDisplayMode ? <PublicAppContent /> : <AppContent />}
    </ToastProvider>
  );
};

export default App;
